/** @jsxImportSource @emotion/react */
import { useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { getSegmentationQueryResponseAsText } from '../../utils/query.response';
import { MessageCitations } from '../Chat.Message.Citations';
import { MessageCitationsToggleContext } from './context';
import { CopyButton } from './Message.CopyToClipboard';
import { SystemUserMessageFooterContainer } from './Message.System.Footer';
import { ChatResponsePane } from './ResponsePane';

type Props = {
  results: Array<{
    dimension: string;
    findings: Array<{
      label: string;
      response: Chat.QueryResponse;
    }>;
  }>;
  citations: Chat.Citation[];
  isCompleted: boolean;
  children: React.ReactNode;
};

export const SegmentationFooter = (props: Props) => {
  const copyToClipboard = useCopyToClipboard();
  const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);
  
  const handleCopy = useCallback(() => {
    copyToClipboard(getSegmentationQueryResponseAsText(props.results));
  }, [copyToClipboard, props.results]);
  
  const view = {
    actions: props.isCompleted,
  };
  
  return (
    <>
      {props.children}
      <SystemUserMessageFooterContainer>
        <>
          {view.actions && (
            <Layer>
              <ActionsContainer>
                <CopyButton onClick={handleCopy} />
              </ActionsContainer>
            </Layer>
          )}
          {!!props.citations.length && (
            <StyledCitationsPane
              title={`Sources (${props.citations.length})`}
              open={citationsOpen}
              toggleOpen={toggleCitationsOpen}>
              <StyledMessageCitations items={props.citations} />
            </StyledCitationsPane>
          )}
        </>
      </SystemUserMessageFooterContainer>
    </>
  );
};

const Layer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(MessageCitations)`
  margin-top: 10px;
`;
